// ExternalSign.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Worker, Viewer, Plugin } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import axios from "axios";
import { PDFDocument } from "pdf-lib";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.js?url";
import "./ExternalSign.css";

// Constants for consistent sizing
const SIG_WIDTH_UI = 180;
const SIG_HEIGHT_UI = 75;
const SIG_WIDTH_PDF = 190; 
const SIG_HEIGHT_PDF = 75;

type SignatureStyle =
  | "bold"
  | "cursive"
  | "minimal"
  | "elegant"
  | "handwritten"
  | "modern"
  | "stamp"
  | "mono"
  | "calligraphy";

const signatureStyles: Record<SignatureStyle, React.CSSProperties> = {
  bold: { fontFamily: "'Arial Black', sans-serif", fontWeight: 900, fontSize: "64px" },
  cursive: { fontFamily: "'Brush Script MT', cursive", fontSize: "78px" },
  minimal: { fontFamily: "'Helvetica Neue', sans-serif", fontWeight: 300, fontSize: "66px" },
  elegant: { fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: "70px" },
  handwritten: { fontFamily: "'Dancing Script', cursive", fontSize: "82px" },
  modern: { fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: "64px" },
  stamp: { fontFamily: "'Impact', sans-serif", fontWeight: 900, fontSize: "62px" },
  mono: { fontFamily: "'Courier New', monospace", fontSize: "64px" },
  calligraphy: { fontFamily: "'Great Vibes', cursive", fontSize: "88px" },
};

export default function ExternalSign() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [docData, setDocData] = useState<{ documentName: string }>({ documentName: "Loading..." });
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [savedSignature, setSavedSignature] = useState<{ url: string; userName: string } | null>(null);
  const [signaturePositions, setSignaturePositions] = useState<Array<{
    id: string;
    x: number;
    y: number;
    page: number;
    pageWidth: number;
    pageHeight: number;
    timestamp: string;
  }>>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<"create-signature" | "place-signature" | "done">("create-signature");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [style, setStyle] = useState<SignatureStyle>("cursive");
  const [selectedSignature, setSelectedSignature] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Custom Signature Plugin to handle real-time overlays without flickering
  const signaturePlugin = (): Plugin => {
    return {
      renderPageLayer: (props) => (
        <div className="custom-signature-overlay">
          {signaturePositions
            .filter((p) => p.page === props.pageIndex)
            .map((pos) => (
              <div
                key={pos.id}
                className="placed-signature"
                style={{
                  left: pos.x - SIG_WIDTH_UI / 2,
                  top: pos.y - SIG_HEIGHT_UI / 2,
                  width: SIG_WIDTH_UI,
                  height: "auto", // Allow height to be determined by content + CSS
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  setSignaturePositions((prev) => prev.filter((p) => p.id !== pos.id));
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={`https://digital-sign-hea5fge9fmchfdav.southeastasia-01.azurewebsites.net//api/proxy-image?url=${encodeURIComponent(
                    savedSignature?.url || ""
                  )}`}
                  alt="Signature"
                  style={{ width: "100%", height: `${SIG_HEIGHT_UI}px`, objectFit: "contain", objectPosition: "left" }}
                />
                <div className="sig-timestamp">{pos.timestamp}</div>
                <div className="sig-help-text">Double-click to remove</div>
              </div>
            ))}
        </div>
      ),
    };
  };

  const signaturePluginInstance = signaturePlugin();

  const generateSignatures = (): string[] => {
    if (!fullName.trim()) return [];
    const words = fullName.trim().split(" ");
    const first = words[0];
    const last = words.slice(-1)[0];
    const fi = first[0];
    const li = last[0];

    return [
      fullName,
      `${first} `,
      `${fi}. ${last}`,
      `${first} ${li}.`,
      `${fi}.${li}.`,
      fullName.toUpperCase(),
    ].filter(Boolean);
  };

  const textToImageBlob = async (text: string): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    canvas.width = 800;
    canvas.height = 250;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas context not available");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "black";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const fontStyle = signatureStyles[style];
    ctx.font = `${fontStyle.fontStyle || "normal"} ${fontStyle.fontWeight || "normal"} ${fontStyle.fontSize} ${fontStyle.fontFamily}`;
    ctx.fillText(text, 20, canvas.height / 2 + 8); // Left-aligned with small padding
    if (canvas.toBlob) {
      return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Blob creation failed"));
        }, "image/png");
      });
    }
    const dataUrl = canvas.toDataURL("image/png");
    const response = await fetch(dataUrl);
    return await response.blob();
  };

  const saveSignature = async () => {
    if (!selectedSignature || !fullName.trim()) {
      alert("Please enter your name and select a signature");
      return;
    }
    setIsSaving(true);
    try {
      const blob = await textToImageBlob(selectedSignature);
      const formData = new FormData();
      formData.append("file", blob, "signature.png");
      formData.append("fullName", fullName.trim());
      formData.append("email", email.trim() || "external@sign.com");
      formData.append("style", style);
      if (id) formData.append("token", id);
      const API_URL = process.env.REACT_APP_API_URL || "https://digital-sign-hea5fge9fmchfdav.southeastasia-01.azurewebsites.net";
      const res = await axios.post(`${API_URL}/api/save-external-signature`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.success && res.data.signatureUrl) {
        setSavedSignature({ url: res.data.signatureUrl, userName: fullName.trim() });
        setStage("place-signature");
      } else {
        alert("Signature saved, but could not retrieve URL.");
      }
    } catch (err: any) {
      console.error("Save failed:", err.response?.data || err.message);
      alert("Error: " + (err.response?.data?.error || err.message || "Failed to save"));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!id) return alert("Invalid link");
      try {
        setLoading(true);
        const url = new URL(window.location.href);
        const emailParam = url.searchParams.get("email") || "";
        const res = await fetch(`https://digital-sign-hea5fge9fmchfdav.southeastasia-01.azurewebsites.net/api/sign-session/${id}${emailParam ? `?email=${encodeURIComponent(emailParam)}` : ""}`);
        if (!res.ok) throw new Error("Document not found or expired");
        const data = await res.json();
        setPdfUrl(data.pdfUrl);
        setDocData({ documentName: data.documentName || "Document" });
        if (data.signatureUrl) {
          setSavedSignature({ url: data.signatureUrl, userName: data.userName || "Signer" });
          setStage("place-signature");
        } else {
          setStage("create-signature");
        }
      } catch (err: any) {
        alert("Error: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (stage !== "place-signature") return;
    const pageLayer = (e.target as HTMLElement).closest(".rpv-core__page-layer");
    if (!pageLayer) return;
    const rect = pageLayer.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const pageIndexAttr = pageLayer.getAttribute("data-page-index");
    const pageIndex = pageIndexAttr ? parseInt(pageIndexAttr, 10) : currentPage;
    const now = new Date();
    const timestamp = `Signed on: ${now.toLocaleDateString('en-GB')}, ${now.toLocaleTimeString('en-GB')}`;
    const newPos = {
      id: `${Date.now()}-${Math.random()}`,
      x: x,
      y: y,
      page: pageIndex,
      pageWidth: rect.width,
      pageHeight: rect.height,
      timestamp: timestamp
    };
    setSignaturePositions(prev => [...prev, newPos]);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (stage === "place-signature") {
      setMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const uint8ToBase64 = (arr: Uint8Array): string => {
    return btoa(Array.from(arr).map(byte => String.fromCharCode(byte)).join(''));
  };

  const generateSignedPDF = async (): Promise<Uint8Array | null> => {
    if (!pdfUrl || !savedSignature || signaturePositions.length === 0) return null;
    try {
      const pdfRes = await fetch(pdfUrl);
      const pdfBytes = new Uint8Array(await pdfRes.arrayBuffer());
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const sigRes = await fetch(`https://digital-sign-hea5fge9fmchfdav.southeastasia-01.azurewebsites.net/api/proxy-image?url=${encodeURIComponent(savedSignature.url)}`);
      if (!sigRes.ok) throw new Error("Failed to load signature");
      const sigBytes = new Uint8Array(await sigRes.arrayBuffer());
      const sigImage = await pdfDoc.embedPng(sigBytes);
      const { StandardFonts, rgb } = await import("pdf-lib");
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      for (const pos of signaturePositions) {
        const page = pdfDoc.getPage(pos.page);
        const { width: pw, height: ph } = page.getSize();
        const scaleX = pw / pos.pageWidth;
        const scaleY = ph / pos.pageHeight;
        const x = (pos.x * scaleX) - ((SIG_WIDTH_PDF / 2) * scaleX);
        const y = ph - (pos.y * scaleY) - ((SIG_HEIGHT_PDF / 2) * scaleY);

        // Draw signature image
        const imgX = Math.max(0, Math.min(x, pw - SIG_WIDTH_PDF * scaleX));
        const imgY = Math.max(0, Math.min(y, ph - SIG_HEIGHT_PDF * scaleY));
        page.drawImage(sigImage, {
          x: imgX,
          y: imgY,
          width: SIG_WIDTH_PDF * scaleX,
          height: SIG_HEIGHT_PDF * scaleY,
        });

        // Draw timestamp text below signature
        const fontSize = 9 * scaleX; // Slightly smaller for professional look
        page.drawText(pos.timestamp, {
          x: imgX + (5 * scaleX), // Small offset to match signature padding
          y: imgY - (10 * scaleY), // Positioned closely below the image
          size: fontSize,
          font: font,
          color: rgb(0, 0, 0),
        });
      }
      return await pdfDoc.save();
    } catch (err) {
      console.error("PDF generation failed:", err);
      return null;
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    const signedBytes = await generateSignedPDF();
    if (signedBytes) {
      const blob = new Blob([signedBytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreviewModal(true);
    } else {
      alert("Failed to generate preview. Please try again.");
    }
    setLoading(false);
  };

  const handleComplete = async () => {
    // Instant Success Transition
    setStage("done");

    // Background processing
    try {
      const signedBytes = await generateSignedPDF();
      if (!signedBytes) throw new Error("Failed to sign document.");

      const blob = new Blob([signedBytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docData.documentName}_signed.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const base64 = uint8ToBase64(signedBytes);

      const uploadRes = await fetch(
        "https://digital-sign-hea5fge9fmchfdav.southeastasia-01.azurewebsites.net/api/upload",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileName: `${docData.documentName}_final-signed.pdf`,
            fileContentBase64: base64,
            token: id,
            role: "external",
          })
        }
      );

      if (uploadRes.ok) {
        const uploadData = await uploadRes.json();
        if (uploadData.success && uploadData.publicPdfUrl) {
          await fetch("https://digital-sign-hea5fge9fmchfdav.southeastasia-01.azurewebsites.net/api/complete-signing", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              itemId: id,
              finalPdfUrl: uploadData.publicPdfUrl
            }),
          });
        }
      }

      setTimeout(() => navigate("/success"), 300);
    } catch (err: any) {
      console.error("HandleComplete error:", err);
      // We don't alert here if stage is already done, to avoid interrupting success
    }
  };

  if (loading && stage !== "done" && !showPreviewModal) {
    return (
      <div className="loading-screen">
        <span className="loader"></span>
        <p style={{ marginTop: "1rem", fontWeight: 600 }}>Preparing document...</p>
      </div>
    );
  }

  return (
    <div className="external-sign-container" onMouseMove={handleMouseMove}>
      <header className="header">
        <h1>{docData.documentName}</h1>
        <div className="stepper">
          <div className={`step ${stage === "create-signature" ? "active" : ""}`}>
            <span className="step-num">1</span> Create Signature
          </div>
          <div className="step-line"></div>
          <div className={`step ${stage === "place-signature" ? "active" : ""}`}>
            <span className="step-num">2</span> Place Signature
          </div>
          <div className="step-line"></div>
          <div className={`step ${stage === "done" ? "active" : ""}`}>
            <span className="step-num">3</span> Complete
          </div>
        </div>
      </header>

      <main className="content">
        {stage === "create-signature" && (
          <div className="create-sign-card">
            <h2>Sign Your Document</h2>
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                placeholder="Type your name..."
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Choose a Style</label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as SignatureStyle)}
              >
                {Object.keys(signatureStyles).map((key) => (
                  <option key={key} value={key}>
                    {key.charAt(0).toUpperCase() + key.slice(1)} Style
                  </option>
                ))}
              </select>
            </div>

            <div className="signature-grid">
              {generateSignatures().length === 0 ? (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "#94a3b8", padding: "3rem", background: "#f8fafc", borderRadius: "1rem", border: "2px dashed #e2e8f0" }}>
                  Start typing your name above to see styles
                </div>
              ) : (
                generateSignatures().map((sig, i) => (
                  <div
                    key={i}
                    className={`sig-preview-card ${selectedSignature === sig ? "selected" : ""}`}
                    onClick={() => setSelectedSignature(sig)}
                  >
                    <div className="sig-text" style={signatureStyles[style]}>
                      {sig}
                    </div>
                  </div>
                ))
              )}
            </div>

            <button
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center", padding: "1.25rem" }}
              onClick={saveSignature}
              disabled={!selectedSignature || isSaving || !fullName.trim()}
            >
              {isSaving ? "Saving..." : "Save & Continue to Signing"}
            </button>
          </div>
        )}

        {stage === "place-signature" && (
          <div className="pdf-viewer-container">
            {signaturePositions.length === 0 && (
              <div className="placement-guidance">
                <span style={{ fontSize: '1.2rem' }}>🖋️</span>
                <span>Click anywhere on the PDF to place your signature.</span>
              </div>
            )}

            {stage === "place-signature" && !showPreviewModal && signaturePositions.length === 0 && (
              <div
                className="mouse-follower"
                style={{ left: mousePos.x, top: mousePos.y }}
              >
                Click to place signature
              </div>
            )}

            <div
              ref={viewerContainerRef}
              onClick={handleClick}
              className="pdf-viewer-wrapper"
            >
              <Worker workerUrl={workerUrl}>
                <Viewer
                  fileUrl={pdfUrl}
                  plugins={[defaultLayoutPluginInstance, signaturePluginInstance]}
                  onPageChange={(e) => setCurrentPage(e.currentPage)}
                />
              </Worker>
            </div>

            <div className="action-bar">
              <button
                className="btn btn-secondary"
                onClick={handlePreview}
                disabled={signaturePositions.length === 0}
              >
                Preview
              </button>
              <button
                className="btn btn-success"
                onClick={handleComplete}
                disabled={signaturePositions.length === 0}
              >
                Complete & Download
              </button>
            </div>
          </div>
        )}

        {stage === "done" && (
          <div className="success-screen">
            <div className="success-icon-container">✓</div>
            <h1>Document Signed Successfully!</h1>
            <p>Thank you for using our Digital Sign System.</p>
            <p style={{ marginTop: '1rem' }}>
              Your document has been signed and saved successfully. A copy of the signed document will be shared with you via email shortly.
            </p>
          </div>
        )}
      </main>

      {/* Preview Modal */}
      {showPreviewModal && previewUrl && (
        <div className="modal-overlay" onClick={() => {
          setShowPreviewModal(false);
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Preview Signed Document</h2>
              <button className="modal-close" onClick={() => setShowPreviewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <Worker workerUrl={workerUrl}>
                <Viewer fileUrl={previewUrl} plugins={[defaultLayoutPluginInstance]} />
              </Worker>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
                Back to Editor
              </button>
              <button className="btn btn-success" onClick={handleComplete}>
                Looks Good, Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
